interface KpiCardProps {
  label: string;
  value: string;
  /** signed delta as a fraction (0.12 → +12%); null hides the delta */
  delta: number | null;
  /** when true, a positive delta is BAD (e.g. cost going up) */
  invertColors?: boolean;
  /** Tooltip-quality footnote shown under the value */
  hint?: string;
  /** Replaces value/delta with skeleton bars while data is in flight */
  loading?: boolean;
}

function formatDelta(delta: number): string {
  const pct = (delta * 100).toFixed(1);
  const sign = delta > 0 ? '+' : '';
  return `${sign}${pct}%`;
}

export function KpiCard({
  label,
  value,
  delta,
  invertColors,
  hint,
  loading,
}: KpiCardProps): JSX.Element {
  if (loading) {
    return (
      <div className="kpi-card">
        <div className="kpi-label">{label}</div>
        <div className="kpi-skeleton-value" aria-hidden="true" />
        <div className="kpi-skeleton-delta" aria-hidden="true" />
        {hint && <div className="kpi-hint">{hint}</div>}
      </div>
    );
  }

  let deltaClass = 'kpi-delta';
  let deltaArrow = '';
  if (delta !== null) {
    const positive = delta > 0;
    const good = invertColors ? !positive : positive;
    if (delta === 0) {
      deltaClass = 'kpi-delta kpi-delta-flat';
      deltaArrow = '→';
    } else {
      deltaClass += good ? ' kpi-delta-good' : ' kpi-delta-bad';
      deltaArrow = positive ? '↑' : '↓';
    }
  }

  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta !== null && (
        <div className={deltaClass}>
          <span aria-hidden="true">{deltaArrow}</span> {formatDelta(delta)}{' '}
          <span className="kpi-delta-suffix">vs prior period</span>
        </div>
      )}
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}
